import { Injectable } from '@angular/core';
import { LineChart } from '@client/app/modules/charts/models/charts/line';
import { ChartsDataHelper } from '../../helpers/charts/charts-data-helper';
import MonColors from '@monsido/colors/dist/es6/colors';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MON_MODULES } from '@monsido/core/constants/mon-modules.constant';
import { SessionService } from '@monsido/core/session/session.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { LegendCursorService } from '@client/deprecate/frontend-components/src/js/components/charts/services/LegendCursor.service';
import { ActiveFeatureService } from '@monsido/ng2/services/active-feature/active-feature.service';
import { HasAccessService } from '@client/app/pipe/has-access/has-access.service';
import { CrawlHistory } from '@monsido/ng2/modules/models/api/interfaces/domain.interface';
import { ChartDataEntry, ChartVerticalLines } from './base-chart/chart-factory';
import { MonNumberService } from '@monsido/ng2/core/locale/services/mon-number.service';
import { Domain } from '@monsido/modules/models/api/domain';

type CrawlHistoryWithPuppeteer20Marker = CrawlHistory & {
    isScanWithV20?: {
        infoLink: string;
    };
    browserServiceChanged?: {
        value: boolean;
        infoLink: string;
    };
    js_rendering_service: 'browser_service' | 'puppeteer_service';
};

@Injectable({
    providedIn: 'root',
})
export class ChangeHistoryLineChartServiceFactory {
    constructor (
        private translateService: TranslateService,
        private sessionService: SessionService,
        private dateTimeService: DateTimeService,
        private activeFeatureService: ActiveFeatureService,
        private legendCursorService: LegendCursorService,
        private hasAccessService: HasAccessService,
        private monNumberService: MonNumberService,
    ) {}

    createChangeHistoryLineChart () {
        return new ChangeHistoryLineChartService(
            this.translateService,
            this.sessionService,
            this.dateTimeService,
            this.activeFeatureService,
            this.legendCursorService,
            this.hasAccessService,
            this.monNumberService,
        );
    }
}

export class ChangeHistoryLineChartService {
    private lineChartSettings: LineChart = new LineChart();
    private domain: Domain | null = null;
    private showDocuments = false;
    private showAccessibility = false;
    private showSEO = false;
    private showPolicy = false;
    private showQA = false;

    constructor (
        private translateService: TranslateService,
        private sessionService: SessionService,
        private dateTimeService: DateTimeService,
        private activeFeatureService: ActiveFeatureService,
        private legendCursorService: LegendCursorService,
        private hasAccessService: HasAccessService,
        private monNumberService: MonNumberService,
    ) {}

    init (domain: Domain) {
        const user = this.sessionService.me;
        this.domain = domain;
        this.showDocuments = Boolean(domain?.scan.scan_documents);
        if (user) {
            this.showAccessibility =
            this.hasAccessService.hasAccess(user, MON_MODULES.accessibility) && Boolean(domain?.features.accessibility);
            this.showSEO =
            this.hasAccessService.hasAccess(user, MON_MODULES.seo) && this.activeFeatureService.isFeatureActivePure('seo_all');
            this.showPolicy =
            this.hasAccessService.hasAccess(user, MON_MODULES.policies) && this.activeFeatureService.isFeatureActivePure('policies_all');
            this.showQA =
            this.hasAccessService.hasAccess(user, MON_MODULES.qa) && this.activeFeatureService.isFeatureActivePure('qa_module');
        }
    }

    /**
     * The option object for the change history line chart
     */
    getOption (history: CrawlHistoryWithPuppeteer20Marker[]) {
        this.lineChartSettings.setTooltipSettings({
            intersect: false,
            mode: 'x-axis',
            callbacks: {
                label: (tooltipItem: any, data: any) => {
                    const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || '';
                    const value = this.monNumberService.transformNumber(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index].y, '0,0');
                    return `${datasetLabel}: ${value}`;
                },
                title: (tooltipItem: any) => {
                    return this.dateTimeService.format(tooltipItem[0].label, 'MMM D, YYYY');
                },
            },
        });
        this.lineChartSettings.setLegendSettingsOverride({
            display: true,
            position: 'bottom',
            labels: {
                boxWidth: 13,
            },
        });
        this.lineChartSettings.setScalesSettings({
            yAxes: [
                {
                    id: 'y-axis-0',
                    type: 'linear',
                    position: 'left',
                    ticks: {
                        fontSize: 11,
                        min: 0,
                        beginAtZero: true,
                        suggestedMax: 5,
                        maxTicksLimit: 5,
                        callback: (value: number) => {
                            return this.monNumberService.transformNumber(value, '0,0');
                        },
                    },
                    gridLines: {
                        drawBorder: false,
                    },
                    scaleLabel: {
                        display: true,
                        labelString: this.translateService.getString('Pages'),
                        fontSize: 11,
                    },
                },
                {
                    id: 'y-axis-1',
                    type: 'linear',
                    display: Boolean(this.showDocuments),
                    position: 'right',
                    ticks: {
                        fontSize: 11,
                        min: 0,
                        beginAtZero: true,
                        suggestedMax: 5,
                        maxTicksLimit: 5,
                        callback: (value: number) => {
                            return this.monNumberService.transformNumber(value, '0,0');
                        },
                    },
                    gridLines: {
                        drawBorder: false,
                        display: false,
                    },
                    scaleLabel: {
                        display: true,
                        labelString: this.translateService.getString('Documents'),
                        fontSize: 11,
                    },
                },
            ],
            xAxes: [
                {
                    id: 'x-axis-0',
                    type: 'time',
                    gridLines: {
                        offsetGridLines: true,
                        drawBorder: false,
                        display: false,
                    },
                    ticks: {
                        fontSize: 11,
                        maxTicksLimit: 30,
                        callback: function (val) {
                            return val;
                        },
                    },
                    time: {
                        minUnit: 'day',
                        unit: 'day',
                        displayFormats: {
                            // Since the scans always only appear once or twice a week, there's no need for any other date formats to be displayed
                            millisecond: 'MMM D',
                            second: 'MMM D',
                            minute: 'MMM D',
                            hour: 'MMM D',
                            day: 'MMM D',
                            week: 'MMM D',
                            month: 'MMM D',
                            quarter: 'MMM D',
                            year: 'MMM D',
                        },
                    },
                },
            ],
        });

        const verticalLines: ChartVerticalLines[] = [];
        for (let i = 0; i < history.length; i++) {
            const entry = history[i];
            if (entry.browserServiceChanged) {
                const browserServiceTranslation = {
                    'browser_service': this.translateService.getString('Legacy Browser Service changed'),
                    'puppeteer_service': this.translateService.getString('Puppeteer Browser Service changed'),
                };
                verticalLines.push({
                    xAxisID: 'x-axis-0',
                    x: entry.date,
                    style: {
                        lineDash: [5, 5],
                        color: MonColors['secondary-15'],
                        width: 3,
                    },
                    label: {
                        text: this.translateService.getString(browserServiceTranslation[entry.js_rendering_service]),
                        color: MonColors['secondary-15'],
                        position: 'top',
                    },
                    data: {
                        link: entry.browserServiceChanged.infoLink,
                    },
                });
            }
            if (entry.isScanWithV20) {
                const v20Text = this.translateService.getString('Puppeteer 20');
                verticalLines.push({
                    xAxisID: 'x-axis-0',
                    x: entry.date,
                    style: {
                        lineDash: [5, 5],
                        color: MonColors['secondary-15'],
                        width: 3,
                    },
                    label: {
                        text: v20Text,
                        color: MonColors['secondary-15'],
                        position: 'top',
                    },
                    data: {
                        link: entry.isScanWithV20.infoLink,
                    },
                });
            }
        }

        this.lineChartSettings.options.verticalLines = {
            lines: verticalLines,
        };

        this.lineChartSettings.options = this.legendCursorService.setHoverState(this.lineChartSettings.options);

        return this.lineChartSettings.options;
    }

    getColors () {
        if (this.showAccessibility) {
            this.lineChartSettings.addColor('#cb8ce2');
        }

        if (this.showSEO) {
            this.lineChartSettings.addColor('#f5c069');
        }

        if (this.showQA) {
            this.lineChartSettings.addColor('#76cdcc');
        }

        if (this.showPolicy) {
            this.lineChartSettings.addColor('#e091b0');
        }

        this.lineChartSettings.addColor('#8ae2ef');

        if (this.showDocuments) {
            this.lineChartSettings.addColor('#57b1b6');
        }
        return this.lineChartSettings.colors;

    }

    getSeries () {
        if (this.showAccessibility) {
            this.lineChartSettings.addSerie(this.translateService.getString('Pages with accessibility issues'));
        }

        if (this.showSEO) {
            this.lineChartSettings.addSerie(this.translateService.getString('Pages with SEO opportunities'));
        }

        if (this.showQA) {
            this.lineChartSettings.addSerie(this.translateService.getString('Pages with QA issues'));
        }

        if (this.showPolicy) {
            this.lineChartSettings.addSerie(this.translateService.getString('Pages with policy issues'));
        }

        this.lineChartSettings.addSerie(this.translateService.getString('Pages crawled'));

        if (this.showDocuments) {
            this.lineChartSettings.addSerie(this.translateService.getString('Documents crawled'));
        }
        return this.lineChartSettings.series;
    }

    getOverride () {
        let linesToOverride = 4;
        if (!this.showAccessibility) {
            linesToOverride = linesToOverride - 1;
        }

        if (!this.showSEO) {
            linesToOverride = linesToOverride - 1;
        }

        if (!this.showPolicy) {
            linesToOverride = linesToOverride - 1;
        }

        if (!this.showQA) {
            linesToOverride = linesToOverride - 1;
        }

        for (let i = 0; i < linesToOverride; i++) {
            this.lineChartSettings.addOverride({
                yAxisID: 'y-axis-0',
                xAxisID: 'x-axis-0',
                type: 'line',
                fill: false,
                lineTension: 0,
                radius: 0,
                borderWidth: 1,
                pointRadius: 3,
                pointHitRadius: 34,
            });
        }

        this.lineChartSettings.addOverride({
            yAxisID: 'y-axis-0',
            xAxisID: 'x-axis-0',
            type: 'line',
            fill: false,
            lineTension: 0,
            radius: 0,
            borderWidth: 1,
            pointRadius: 3,
            pointHitRadius: 34,
        });

        if (this.showDocuments) {
            this.lineChartSettings.addOverride({
                yAxisID: 'y-axis-1',
                xAxisID: 'x-axis-0',
                type: 'line',
                fill: false,
                lineTension: 0,
                radius: 0,
                borderWidth: 1,
                pointRadius: 3,
                pointHitRadius: 34,
            });
        }

        return this.lineChartSettings.override;
    }

    getLabels (crawls: CrawlHistory[]) {
        return crawls.map((crawl) => crawl.date);
    }

    getData (crawls: CrawlHistory[]): ChartDataEntry[] {
        const helper = new ChartsDataHelper('time');
        const data: ChartDataEntry[] = [];
        const copiedCrawls = [].slice.call(crawls).map(function (crawl) {
            const newCrawl = Object.assign({}, crawl);
            if (newCrawl.queued_at) {
                newCrawl.date = newCrawl.queued_at;
            }
            return newCrawl;
        });
        if (this.showAccessibility) {
            data.push(
                copiedCrawls.map((crawl) => {
                    return helper.setData(crawl, 'pages_with_accessibility_errors_count');
                }),
            );
        }
        if (this.showSEO) {
            data.push(
                copiedCrawls.map((crawl) => {
                    return helper.setData(crawl, 'pages_with_seo_errors_count');
                }), // All classifications except Technical
            );
        }
        if (this.showQA) {
            data.push(
                copiedCrawls.map((crawl) => {
                    return helper.setData(crawl, 'pages_with_notifications_count');
                }),
            );
        }
        if (this.showPolicy) {
            data.push(
                copiedCrawls.map((crawl) => {
                    return helper.setData(crawl, 'pages_with_policy_matches_count');
                }),
            );
        }
        data.push(
            copiedCrawls.map((crawl) => {
                return helper.setData(crawl, 'page_count');
            }),
        );

        if (this.showDocuments) {
            data.push(
                copiedCrawls.map((crawl) => {
                    return helper.setData(crawl, 'documents_count');
                }),
            );
        }
        return data;
    }
}
